/* ==========================================================================
   iTechVista — cart enhancement
   --------------------------------------------------------------------------
   Companion to sections/itv-cart-items.liquid. It also drives the note and the
   totals rendered by sections/itv-cart-footer.liquid, because those live
   outside the shared <form id="itv-cart-form"> and there is exactly one cart
   controller per page.

   Everything this file does is an ENHANCEMENT of markup that already works:

     - quantities are real <input name="updates[]"> fields in a real form
     - removal is a real link to item.url_to_remove
     - a visible "Update" submit button applies the whole form

   So the rule here is: never break the fallback. If this file fails to load,
   is blocked, or throws, the page still updates the cart on submit. The only
   thing it changes structurally is hiding the Update button (via the
   [data-itv-cart-enhanced] attribute the CSS keys off), and it only does that
   after it has successfully installed its own handlers.

   Totals are re-rendered by asking the Section Rendering API for the sections
   that advertise [data-itv-cart-section], never by formatting money in JS —
   the shop's money format, currency and locale belong to Liquid.

   Failures are shown, not swallowed: the message Shopify returns in the 422
   body (`description`) is what the shopper reads. When Shopify grants fewer
   units than were asked for, the granted number is read back out of the
   response and reported on that line.
   ========================================================================== */
(function () {
  'use strict';

  var FALLBACK_STRINGS = {
    error: 'There was an error while updating your cart. Please try again.',
    quantityError: 'You can only add [quantity] of this item to your cart.',
    updated: 'Cart updated.',
    removed: 'Item removed from your cart.',
    noteSaved: 'Order note saved.'
  };

  var strings = FALLBACK_STRINGS;
  var busy = false;

  /* At most one cart request is in flight at a time; an interaction that
     arrives during one is held here and replayed when it settles, rather than
     being thrown away. */
  var pending = null;

  function settle() {
    var next = pending;
    pending = null;
    if (next) next();
  }

  /* ----------------------------------------------------------- utilities */

  function sectionRoots() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-itv-cart-section]'));
  }

  function rootWithData(attribute) {
    var roots = sectionRoots();
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].getAttribute(attribute)) return roots[i];
    }
    return null;
  }

  function endpoint(attribute, fallback) {
    var root = rootWithData(attribute);
    var value = root ? root.getAttribute(attribute) : '';
    return (value || fallback) + '.js';
  }

  /* Line item keys contain a colon, which querySelector would read as a
     pseudo-class, so match on the attribute value instead of building a
     selector out of it. */
  function findByKey(selector, key) {
    var nodes = document.querySelectorAll(selector);
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].getAttribute('data-itv-line-key') === key) return nodes[i];
    }
    return null;
  }

  function readStrings() {
    var node = document.querySelector('[data-itv-cart-strings]');
    if (!node) return FALLBACK_STRINGS;
    try {
      var parsed = JSON.parse(node.textContent);
      var merged = {};
      for (var key in FALLBACK_STRINGS) {
        if (Object.prototype.hasOwnProperty.call(FALLBACK_STRINGS, key)) {
          merged[key] = parsed[key] || FALLBACK_STRINGS[key];
        }
      }
      return merged;
    } catch (error) {
      return FALLBACK_STRINGS;
    }
  }

  /* ------------------------------------------------------- announcements */

  function announce(message) {
    var status = document.querySelector('[data-itv-cart-status]');
    if (!status || !message) return;
    // Clearing first guarantees the region is seen to change even when the
    // same sentence is announced twice in a row.
    status.textContent = '';
    window.setTimeout(function () {
      status.textContent = message;
    }, 60);
  }

  function showError(message) {
    var alert = document.querySelector('[data-itv-cart-alert]');
    if (!alert) return;
    alert.textContent = message;
    alert.hidden = false;
  }

  function clearError() {
    var alert = document.querySelector('[data-itv-cart-alert]');
    if (!alert) return;
    alert.textContent = '';
    alert.hidden = true;
  }

  function showLineError(key, message) {
    var row = findByKey('[data-itv-cart-line]', key);
    if (!row) return;
    var slot = row.querySelector('[data-itv-cart-line-error]');
    if (!slot) return;
    slot.innerHTML = message;
    slot.hidden = false;
  }

  /* ------------------------------------------------------------ in-flight */

  function setBusy(state) {
    busy = state;
    var roots = sectionRoots();

    for (var i = 0; i < roots.length; i++) {
      var root = roots[i];
      root.setAttribute('aria-busy', state ? 'true' : 'false');

      var controls = root.querySelectorAll('input, button, select, textarea');
      for (var c = 0; c < controls.length; c++) {
        var control = controls[c];
        if (state) {
          // Only tag what this file actually disabled, so releasing never
          // enables something Liquid disabled on purpose — an empty cart's
          // checkout button, say.
          if (!control.disabled) {
            control.disabled = true;
            control.setAttribute('data-itv-disabled-by-js', 'true');
          }
        } else if (control.hasAttribute('data-itv-disabled-by-js')) {
          control.disabled = false;
          control.removeAttribute('data-itv-disabled-by-js');
        }
      }

      // Links cannot be disabled; mark them and let the click guard refuse.
      var links = root.querySelectorAll('[data-itv-cart-remove]');
      for (var l = 0; l < links.length; l++) {
        if (state) {
          links[l].setAttribute('aria-disabled', 'true');
        } else {
          links[l].removeAttribute('aria-disabled');
        }
      }
    }
  }

  /* -------------------------------------------------------- section swap */

  function noteState() {
    var note = document.querySelector('[data-itv-cart-note]');
    if (!note || !note.hasAttribute('data-itv-note-dirty')) return null;
    return { value: note.value, focused: document.activeElement === note };
  }

  function restoreNote(state) {
    if (!state) return;
    var note = document.querySelector('[data-itv-cart-note]');
    if (!note) return;
    // The shopper's unsaved text outranks whatever the server just rendered.
    note.value = state.value;
    note.setAttribute('data-itv-note-dirty', 'true');
    if (state.focused) note.focus();
  }

  /* Returns how many sections were actually replaced, because zero means the
     screen no longer matches the cart and the caller has to fall back. */
  function swapSections(rendered) {
    if (!rendered) return 0;

    var note = noteState();
    var roots = sectionRoots();
    var swapped = 0;

    for (var i = 0; i < roots.length; i++) {
      var root = roots[i];
      var id = root.getAttribute('data-itv-cart-section-id');
      var html = id ? rendered[id] : null;
      if (!html) continue;

      var incoming = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('[data-itv-cart-swap]');
      var current = root.querySelector('[data-itv-cart-swap]');
      if (!incoming || !current) continue;

      current.innerHTML = incoming.innerHTML;
      swapped++;
    }

    restoreNote(note);
    return swapped;
  }

  function sectionIds() {
    var ids = [];
    var roots = sectionRoots();
    for (var i = 0; i < roots.length; i++) {
      var id = roots[i].getAttribute('data-itv-cart-section-id');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    }
    return ids;
  }

  /* --------------------------------------------------------- focus return */

  function restoreFocus(key, rowIndex) {
    var input = findByKey('[data-itv-cart-quantity]', key);
    if (input) {
      input.focus();
      return;
    }

    // That line is gone. Land on whatever took its place, or on the way out
    // of an emptied cart.
    var rows = document.querySelectorAll('[data-itv-cart-line]');
    if (rows.length) {
      var index = Math.min(Math.max(rowIndex - 1, 0), rows.length - 1);
      var next = rows[index].querySelector('[data-itv-cart-quantity]');
      if (next) next.focus();
      return;
    }

    var link = document.querySelector('[data-itv-cart-empty-link]');
    if (link) link.focus();
  }

  /* ------------------------------------------------------------- requests */

  function post(url, payload) {
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(payload)
    }).then(function (response) {
      return response.text().then(function (text) {
        var data = null;
        try {
          data = JSON.parse(text);
        } catch (error) {
          data = null;
        }

        if (!response.ok) {
          // Shopify's 422 body carries the real reason in `description`.
          var reason =
            (data && (data.description || data.message)) || 'HTTP ' + response.status;
          throw new Error(reason);
        }

        return data;
      });
    });
  }

  function changeLine(key, quantity, rowIndex, requested) {
    if (busy) {
      // Do not drop the interaction: a blur that fires a quantity change while
      // the shopper is already clicking Remove would otherwise look like the
      // click did nothing.
      pending = function () {
        changeLine(key, quantity, rowIndex, requested);
      };
      return;
    }

    clearError();
    setBusy(true);

    post(endpoint('data-itv-cart-change-url', '/cart/change'), {
      id: key,
      quantity: quantity,
      sections: sectionIds().join(','),
      sections_url: window.location.pathname + window.location.search
    })
      .then(function (cart) {
        var swapped = swapSections(cart && cart.sections);
        setBusy(false);

        if (!swapped) {
          // The cart changed but the page could not be re-rendered. Showing
          // stale quantities and totals would be a lie, so start over.
          window.location.reload();
          return;
        }

        restoreFocus(key, rowIndex);

        var granted = null;
        if (cart && cart.items) {
          for (var i = 0; i < cart.items.length; i++) {
            if (cart.items[i].key === key) {
              granted = cart.items[i].quantity;
              break;
            }
          }
        }

        if (quantity === 0) {
          announce(strings.removed);
        } else if (granted !== null && granted < requested) {
          // Fewer units than asked for: say so on the line and out loud.
          var message = strings.quantityError.replace('[quantity]', String(granted));
          showLineError(key, message);
          announce(message.replace(/<[^>]*>/g, ''));
        } else {
          announce(strings.updated);
        }

        document.dispatchEvent(
          new CustomEvent('itv:cart:updated', { detail: { cart: cart }, bubbles: true })
        );
        settle();
      })
      .catch(function (error) {
        setBusy(false);

        // Put the field back the way the server still believes it is.
        var input = findByKey('[data-itv-cart-quantity]', key);
        if (input) {
          input.value = input.getAttribute('data-itv-previous') || input.defaultValue;
          input.focus();
        }

        var message = (error && error.message) || strings.error;
        showError(message);
        announce(message);
        settle();
      });
  }

  function saveNote(note) {
    if (busy) {
      var value = note.value;
      pending = function () {
        var current = document.querySelector('[data-itv-cart-note]');
        if (current) {
          current.value = value;
          saveNote(current);
        }
      };
      return;
    }

    clearError();
    setBusy(true);

    post(endpoint('data-itv-cart-update-url', '/cart/update'), { note: note.value })
      .then(function () {
        setBusy(false);
        var current = document.querySelector('[data-itv-cart-note]');
        if (current) current.removeAttribute('data-itv-note-dirty');
        announce(strings.noteSaved);
        settle();
      })
      .catch(function (error) {
        setBusy(false);
        // The note is still in the textarea and still in the form, so the
        // next submit will carry it; say what went wrong and move on.
        var message = (error && error.message) || strings.error;
        showError(message);
        announce(message);
        settle();
      });
  }

  /* ------------------------------------------------------------- handlers */

  function onChange(event) {
    var target = event.target;
    if (!target || !target.getAttribute) return;

    if (target.hasAttribute('data-itv-cart-quantity')) {
      var previous = parseInt(target.getAttribute('data-itv-previous'), 10);
      var requested = parseInt(target.value, 10);

      if (isNaN(requested) || requested < 0) {
        target.value = isNaN(previous) ? target.defaultValue : previous;
        return;
      }
      if (requested === previous) return;

      changeLine(
        target.getAttribute('data-itv-line-key'),
        requested,
        parseInt(target.getAttribute('data-itv-line-index'), 10) || 1,
        requested
      );
      return;
    }

    if (target.hasAttribute('data-itv-cart-note')) {
      saveNote(target);
    }
  }

  function onInput(event) {
    var target = event.target;
    if (target && target.getAttribute && target.hasAttribute('data-itv-cart-note')) {
      target.setAttribute('data-itv-note-dirty', 'true');
    }
  }

  function onClick(event) {
    var trigger = event.target.closest ? event.target.closest('[data-itv-cart-remove]') : null;
    if (!trigger) return;

    // Always take over the link: following it mid-request would race with the
    // in-flight change and land on a stale cart.
    event.preventDefault();
    if (busy) return;

    changeLine(
      trigger.getAttribute('data-itv-line-key'),
      0,
      parseInt(trigger.getAttribute('data-itv-line-index'), 10) || 1,
      0
    );
  }

  /* ----------------------------------------------------------------- init */

  function init() {
    var roots = sectionRoots();
    if (!roots.length) return;
    if (typeof fetch !== 'function' || typeof DOMParser !== 'function') return;

    strings = readStrings();

    document.addEventListener('change', onChange);
    document.addEventListener('input', onInput);
    document.addEventListener('click', onClick);

    // Only now is the no-JS Update button redundant.
    for (var i = 0; i < roots.length; i++) {
      roots[i].setAttribute('data-itv-cart-enhanced', 'true');
    }
  }

  init();
})();
